import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { BudgetProgress } from '@/components/ui/Progress';
import { EditableAmount } from '@/components/transaction/EditableAmount';
import { BudgetNodeIcon } from '@/components/budget/BudgetNodeIcon';
import { useUpdateBudget } from '@/hooks/useBudgets';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { SharedBudgetBadge } from '@/components/sharing/SharingUi';
import type { Budget, BudgetStatus, UpdateBudgetRequest } from '@/types';

interface BudgetCardProps {
  budget: Budget;
  onClick?: () => void;
  onToggle?: () => void;
  expanded?: boolean;
  actions?: React.ReactNode;
  originalCurrency?: string;
  originalAmounts?: {
    spent: number;
    remaining: number;
    budget: number;
  };
  secondaryCurrency?: string;
  secondaryAmounts?: {
    spent: number;
    remaining: number;
    budget: number;
  };
  titleEditable?: boolean;
  onTransactionDrop?: (transactionId: string) => void;
  /** Drop a transaction onto a child in the inline summary */
  onChildTransactionDrop?: (transactionId: string, childId: string) => void;
  /** Whether the card is currently selected in selection mode */
  isSelected?: boolean;
  /** Whether the app is in budget selection mode */
  selectionMode?: boolean;
  /** Called when user toggles selection for this budget */
  onToggleSelect?: () => void;
  /** Selected child ids when showChildSummary is used with selection */
  selectedChildIds?: string[];
  /** Toggle selection for an inline child row */
  onToggleSelectChild?: (childId: string) => void;
  /** Called when user chooses Select from the ⋮ menu */
  onEnterSelection?: () => void;
  /** Show a summary of child budgets inline (budgets list page) */
  showChildSummary?: boolean;
}

export function BudgetCard({
  budget,
  onClick,
  onToggle,
  expanded,
  actions,
  originalCurrency,
  originalAmounts,
  secondaryCurrency,
  secondaryAmounts,
  titleEditable = true,
  onTransactionDrop,
  onChildTransactionDrop,
  isSelected = false,
  selectionMode = false,
  onToggleSelect,
  selectedChildIds = [],
  onToggleSelectChild,
  onEnterSelection,
  showChildSummary = false,
}: BudgetCardProps) {
  const navigate = useNavigate();
  const updateBudget = useUpdateBudget();
  const spent = budget.spent ?? 0;
  const remaining = budget.remaining ?? budget.unallocated ?? (budget.amount - spent);
  const percentSpent = budget.percentSpent ?? (budget.amount > 0 ? (spent / budget.amount) * 100 : 0);
  const isCompleted = remaining === 0;
  const isOver = remaining < 0;
  const status = isOver
    ? 'OVER_BUDGET'
    : isCompleted
    ? 'ON_TRACK'
    : (budget.status ?? getStatus(percentSpent, budget.type));

  const baseCurr = originalCurrency ?? budget.currency;
  const baseRemaining = originalAmounts?.remaining ?? remaining;
  const baseBudgetAmount = originalAmounts?.budget ?? budget.amount;

  const hasChildren = (budget.children?.length ?? 0) > 0;
  const childCount = budget.children?.length ?? 0;
  const [isExpanded, setIsExpanded] = React.useState(expanded ?? true);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const dragEnterCount = React.useRef(0);

  React.useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const updateBudgetCache = async (nextBudgetAmount: number) => {
    const request: UpdateBudgetRequest = {
      name: budget.name,
      type: budget.type,
      parentId: budget.parentId ?? undefined,
      currency: budget.currency,
      amount: nextBudgetAmount,
      startDate: budget.startDate,
      endDate: budget.endDate,
      rolloverEnabled: budget.rolloverEnabled ?? false,
      rolloverAmount: budget.rolloverAmount ?? undefined,
      version: budget.version ?? 0,
    };

    await updateBudget.mutateAsync({ id: budget.id, request });
  };

  const handleSaveBudgetAmount = async (nextAmount: number) => {
    if (nextAmount === baseBudgetAmount) {
      return;
    }

    await updateBudgetCache(nextAmount);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    navigate(`/budgets/${budget.id}`);
  };

  const Content = (
    <Card
      data-testid={`budget-card-${budget.id}`}
      className={cn(
        'relative',
        'transition-all hover:shadow-md',
        'active:scale-[0.99]',
        isDragOver && 'ring-2 ring-primary ring-offset-1 shadow-lg',
        isSelected && 'ring-2 ring-primary ring-offset-1'
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (selectionMode) {
          e.preventDefault();
          onToggleSelect?.();
        } else {
          handleCardClick();
        }
      }}
      onDragEnter={(e) => {
        if (!onTransactionDrop) return;
        if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
        e.preventDefault();
        dragEnterCount.current += 1;
        setIsDragOver(true);
      }}
      onDragLeave={() => {
        if (!onTransactionDrop) return;
        dragEnterCount.current -= 1;
        if (dragEnterCount.current === 0) setIsDragOver(false);
      }}
      onDragOver={(e) => {
        if (!onTransactionDrop) return;
        if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        if (!onTransactionDrop) return;
        e.preventDefault();
        e.stopPropagation();
        dragEnterCount.current = 0;
        setIsDragOver(false);
        const transactionId = e.dataTransfer.getData('application/x-transaction-id');
        if (transactionId) onTransactionDrop(transactionId);
      }}
    >
      <CardContent className="p-0">
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-primary/10 border-2 border-dashed border-primary pointer-events-none">
            <span className="text-sm font-semibold text-primary">Drop here → {budget.name}</span>
          </div>
        )}
        <div
          className={cn(
            'p-3 pb-0 md:p-3 md:pb-0',
            // In selection mode, block nested links/controls in the header; card onClick toggles selection.
            selectionMode && '[&_a]:pointer-events-none [&_button]:pointer-events-none'
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-1.5 gap-2">
            <div className="flex flex-1 min-w-0 items-start gap-2">
              {selectionMode && (
                <div
                  className={cn(
                    'mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-muted-foreground bg-background'
                  )}
                  aria-hidden="true"
                >
                  {isSelected && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="block font-display text-xl font-semibold tracking-tight text-foreground truncate md:text-2xl">
                  {titleEditable ? (
                    <Link
                      to={`/budgets/${budget.id}/edit`}
                      className="hover:text-primary cursor-pointer"
                      title="Edit budget"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {budget.name}
                    </Link>
                  ) : (
                    budget.name
                  )}
                </h3>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <span className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    budget.type === 'OBLIGATION'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-secondary/40 text-foreground'
                  )}>
                    {budget.type === 'OBLIGATION' ? 'Obligation' : 'Discretionary'}
                  </span>
                  <SharedBudgetBadge shared={budget.shared} owned={budget.owned} />
                  {hasChildren && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
                      {childCount} sub-budget{childCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                {actions}
                {!selectionMode && onEnterSelection && (
                  <div className="relative" ref={menuRef}>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Budget actions"
                      aria-expanded={menuOpen}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setMenuOpen((open) => !open);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="5" r="1" />
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="19" r="1" />
                      </svg>
                    </button>
                    {menuOpen && (
                      <div
                        className="absolute right-0 top-full z-40 mt-1 min-w-[140px] rounded-lg border border-border bg-background py-1 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-left hover:bg-muted"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setMenuOpen(false);
                            onEnterSelection();
                          }}
                        >
                          Select
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <StatusBadge status={status} />
            </div>
          </div>
        </div>

        {/* Amount info — Option A meter + single Total */}
        <div className="px-3 pb-3 pt-1 space-y-1.5 md:px-3">
          <BudgetProgress
            spent={spent}
            directSpent={budget.directSpent}
            allocated={budget.allocated}
            budget={baseBudgetAmount}
            currency={baseCurr}
            percentSpent={percentSpent}
            remaining={baseRemaining}
            unallocated={budget.unallocated}
            secondaryCurrency={secondaryCurrency}
            secondarySpent={secondaryAmounts?.spent}
            secondaryRemaining={secondaryAmounts?.remaining}
            secondaryBudget={secondaryAmounts?.budget}
          />
          <div
            className="flex items-center justify-between gap-2 text-sm"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span className="text-muted-foreground">Total</span>
            <div className="flex items-center gap-1 whitespace-nowrap">
              <EditableAmount
                amount={baseBudgetAmount}
                currency={baseCurr}
                onSave={handleSaveBudgetAmount}
                disabled={updateBudget.isPending}
              />
              {secondaryCurrency && secondaryAmounts?.budget !== undefined && (
                <span className="text-xs text-muted-foreground">
                  ({formatCurrency(secondaryAmounts.budget, secondaryCurrency)})
                </span>
              )}
            </div>
          </div>
          {!budget.parentId && (
            <div className="text-xs text-muted-foreground">
              {formatDate(budget.startDate)} - {formatDate(budget.endDate)}
            </div>
          )}
        </div>
        {showChildSummary && hasChildren && (
          <div
            className="border-t border-border bg-muted/30 px-3 py-2 space-y-1"
            data-testid={`budget-child-summary-${budget.id}`}
          >
            {budget.children!.map((child) => (
              <ChildSummaryRow
                key={child.id}
                child={child}
                selectionMode={selectionMode}
                isSelected={selectedChildIds.includes(child.id)}
                onToggleSelect={
                  onToggleSelectChild ? () => onToggleSelectChild(child.id) : undefined
                }
                onTransactionDrop={
                  onChildTransactionDrop
                    ? (txId) => onChildTransactionDrop(txId, child.id)
                    : undefined
                }
              />
            ))}
          </div>
        )}
        {hasChildren && onToggle && (
          <button
            type="button"
            className="relative z-30 flex w-full items-center justify-center border-t border-border bg-accent/10 px-4 py-2.5 text-center hover:bg-accent/20"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onToggle();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse child budgets' : 'Expand child budgets'}
            title={isExpanded ? 'Collapse child budgets' : 'Expand child budgets'}
          >
            <span className="text-sm font-semibold text-foreground">
              {isExpanded ? '( - )' : '( + )'}
            </span>
          </button>
        )}
      </CardContent>
    </Card>
  );

  // Avoid wrapping interactive controls (⋮ menu) in <a> — invalid HTML; click may navigate.
  if (onClick || hasChildren || selectionMode || onEnterSelection || showChildSummary) {
    return Content;
  }

  return (
    <Link to={`/budgets/${budget.id}`} className="block">
      {Content}
    </Link>
  );
}

function ChildSummaryRow({
  child,
  selectionMode,
  isSelected,
  onToggleSelect,
  onTransactionDrop,
}: {
  child: Budget;
  selectionMode: boolean;
  isSelected: boolean;
  onToggleSelect?: () => void;
  onTransactionDrop?: (transactionId: string) => void;
}) {
  const [isDragOver, setIsDragOver] = React.useState(false);
  const dragEnterCount = React.useRef(0);
  const childSpent = child.spent ?? 0;
  const childRemaining = child.remaining ?? (child.amount - childSpent);
  const childHasChildren = (child.children?.length ?? 0) > 0;

  const rowClass = cn(
    'flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm transition-colors',
    'hover:bg-muted/60',
    isDragOver && 'ring-2 ring-primary bg-primary/10',
    selectionMode && isSelected && 'bg-primary/5'
  );

  const content = (
    <>
      <span className="flex items-center gap-2 min-w-0">
        {selectionMode && (
          <span
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors',
              isSelected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-muted-foreground bg-background'
            )}
            aria-hidden="true"
          >
            {isSelected && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            )}
          </span>
        )}
        <BudgetNodeIcon hasChildren={childHasChildren} size={13} />
        <span className="truncate font-medium text-foreground">{child.name}</span>
      </span>
      <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
        {formatCurrency(childSpent, child.currency)} / {formatCurrency(child.amount, child.currency)}
        {childRemaining < 0 && (
          <span className="ml-1 text-destructive">
            ({formatCurrency(Math.abs(childRemaining), child.currency)} over)
          </span>
        )}
      </span>
    </>
  );

  const dragProps = onTransactionDrop
    ? {
        onDragEnter: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
          e.preventDefault();
          e.stopPropagation();
          dragEnterCount.current += 1;
          setIsDragOver(true);
        },
        onDragLeave: (e: React.DragEvent) => {
          e.stopPropagation();
          dragEnterCount.current -= 1;
          if (dragEnterCount.current === 0) setIsDragOver(false);
        },
        onDragOver: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'move';
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          dragEnterCount.current = 0;
          setIsDragOver(false);
          const transactionId = e.dataTransfer.getData('application/x-transaction-id');
          if (transactionId) onTransactionDrop(transactionId);
        },
      }
    : {};

  if (selectionMode) {
    return (
      <button
        type="button"
        data-testid={`budget-child-row-${child.id}`}
        className={cn(rowClass, 'w-full text-left')}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onToggleSelect?.();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        {...dragProps}
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={`/budgets/${child.id}`}
      data-testid={`budget-child-row-${child.id}`}
      onClick={(e) => e.stopPropagation()}
      className={rowClass}
      {...dragProps}
    >
      {content}
    </Link>
  );
}

function getStatus(percentSpent: number, type: string): BudgetStatus {
  if (percentSpent > 100) return 'OVER_BUDGET';
  if (percentSpent === 100) return 'ON_TRACK';
  if (percentSpent >= 90) return 'NEAR_LIMIT';
  if (type === 'OBLIGATION' && percentSpent < 50) return 'AHEAD';
  return 'ON_TRACK';
}

interface StatusBadgeProps {
  status: BudgetStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config: Record<BudgetStatus, { label: string; className: string }> = {
    ON_TRACK: {
      label: 'On Track',
      className: 'bg-success/10 text-success',
    },
    NEAR_LIMIT: {
      label: 'Near Limit',
      className: 'bg-warning/10 text-warning',
    },
    OVER_BUDGET: {
      label: 'Over Budget',
      className: 'bg-destructive/10 text-destructive',
    },
    AHEAD: {
      label: 'Ahead',
      className: 'bg-success/10 text-success',
    },
  };

  const { label, className } = config[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        className
      )}
    >
      {label}
    </span>
  );
}
